import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import limiteTamanhoCampos
  from '../limiteCampos';

import {
  serviceRecuperaOperacoes,
} from '../Service/service';

import {
  serviceRecuperaBancosBrasil,
} from '../Service/brasilApiService';

const normalizarTexto = (texto) => {
  return String(texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
};

const obterIdentificadorBanco = (banco) => {
  if (!banco) {
    return '';
  }

  const ispb = String(
    banco.ispb || '',
  ).trim();

  if (ispb) {
    return ispb;
  }

  return `${String(
    banco.codigo || '',
  ).trim()}-${normalizarTexto(
    banco.nome,
  )}`;
};

const FormularioBancario = forwardRef(
  (props, ref) => {
    const refNacional = 'nacional';

    const [
      formData,
      setFormData,
    ] = useState(
      props.formData || {},
    );

    const [
      bancos,
      setBancos,
    ] = useState([]);

    const [
      bancoSelecionadoId,
      setBancoSelecionadoId,
    ] = useState('');

    const [
      operacoesCaixa,
      setOperacoesCaixa,
    ] = useState([]);

    const [
      carregandoBancos,
      setCarregandoBancos,
    ] = useState(false);

    const [
      carregandoOperacoes,
      setCarregandoOperacoes,
    ] = useState(false);

    const [
      erroBancos,
      setErroBancos,
    ] = useState('');

    const [
      erroOperacoes,
      setErroOperacoes,
    ] = useState('');

    const [
      validacao,
      setValidacao,
    ] = useState({
      bancoNacional:
        props.nacionalidade !==
        refNacional,

      bancoCodigoNacional:
        props.nacionalidade !==
        refNacional,

      bancoAgenciaNacional:
        props.nacionalidade !==
        refNacional,

      bancoContaNacional:
        props.nacionalidade !==
        refNacional,

      bancoContaDvNacional:
        props.nacionalidade !==
        refNacional,

      bancoTipoContaNacional:
        props.nacionalidade !==
        refNacional,

      bancoNaturezaContaNacional:
        props.nacionalidade !==
        refNacional,

      bancoPosicaoContaNacional:
        props.nacionalidade !==
        refNacional,

      numeroOperacaoNacional:
        props.nacionalidade !==
        refNacional,

      bancoInternacional:
        props.nacionalidade ===
        refNacional,

      bancoAgenciaInternacional:
        props.nacionalidade ===
        refNacional,

      bancoIbanInternacional:
        props.nacionalidade ===
        refNacional,

      bancoContaInternacional:
        props.nacionalidade ===
        refNacional,

      bancoSwiftInternacional:
        props.nacionalidade ===
        refNacional,

      bancoTipoContaInternacional:
        props.nacionalidade ===
        refNacional,

      bancoNaturezaContaInternacional:
        props.nacionalidade ===
        refNacional,

      bancoPosicaoContaInternacional:
        props.nacionalidade ===
        refNacional,
    });

    /*
     * Mantém os dados do formulário sincronizados caso
     * props.formData seja alterado pelo componente pai.
     */
    useEffect(() => {
      setFormData(
        props.formData || {},
      );
    }, [props.formData]);

    /*
     * Carrega os bancos brasileiros pela BrasilAPI.
     */
    useEffect(() => {
      const controller =
        new AbortController();

      const carregarBancos =
        async () => {
          setCarregandoBancos(true);
          setErroBancos('');

          try {
            const resultado =
              await serviceRecuperaBancosBrasil(
                controller.signal,
              );

            const bancosCarregados =
              Array.isArray(resultado)
                ? resultado
                : [];

            setBancos(
              bancosCarregados,
            );
          } catch (error) {
            if (
              error?.name ===
              'AbortError'
            ) {
              return;
            }

            console.error(
              'Erro ao recuperar bancos da BrasilAPI:',
              error,
            );

            setBancos([]);

            setErroBancos(
              'Não foi possível carregar a lista de bancos. Tente novamente mais tarde.',
            );
          } finally {
            if (
              !controller
                .signal
                .aborted
            ) {
              setCarregandoBancos(
                false,
              );
            }
          }
        };

      carregarBancos();

      return () => {
        controller.abort();
      };
    }, []);

    /*
     * Sincroniza o banco salvo no formulário com a opção
     * correspondente retornada pela BrasilAPI.
     *
     * Isso evita o aviso "out-of-range value" do Material UI
     * quando o nome salvo possui capitalização diferente,
     * por exemplo: "CÂMARA B3" e "Câmara B3".
     */
    useEffect(() => {
      if (!bancos.length) {
        setBancoSelecionadoId('');
        return;
      }

      const ispbSalvo = String(
        formData
          ?.bancoIspbNacional ||
        '',
      ).trim();

      const codigoSalvo = String(
        formData
          ?.bancoCodigoNacional ||
        '',
      ).trim();

      const nomeSalvo =
        normalizarTexto(
          formData
            ?.bancoNacional,
        );

      let bancoCorrespondente = null;

      if (ispbSalvo) {
        bancoCorrespondente =
          bancos.find(
            (banco) =>
              String(
                banco.ispb || '',
              ).trim() ===
              ispbSalvo,
          ) || null;
      }

      if (
        !bancoCorrespondente &&
        nomeSalvo
      ) {
        bancoCorrespondente =
          bancos.find(
            (banco) =>
              normalizarTexto(
                banco.nome,
              ) === nomeSalvo,
          ) || null;
      }

      if (
        !bancoCorrespondente &&
        codigoSalvo
      ) {
        const bancosComMesmoCodigo =
          bancos.filter(
            (banco) =>
              String(
                banco.codigo || '',
              ).trim() ===
              codigoSalvo,
          );

        if (
          bancosComMesmoCodigo
            .length === 1
        ) {
          [
            bancoCorrespondente,
          ] =
            bancosComMesmoCodigo;
        }
      }

      if (!bancoCorrespondente) {
        setBancoSelecionadoId('');
        return;
      }

      const identificador =
        obterIdentificadorBanco(
          bancoCorrespondente,
        );

      setBancoSelecionadoId(
        identificador,
      );

      const nomeAtual = String(
        formData
          ?.bancoNacional ||
        '',
      );

      const codigoAtual = String(
        formData
          ?.bancoCodigoNacional ||
        '',
      );

      const ispbAtual = String(
        formData
          ?.bancoIspbNacional ||
        '',
      );

      const novoNome =
        bancoCorrespondente.nome;

      const novoCodigo = String(
        bancoCorrespondente.codigo,
      );

      const novoIspb = String(
        bancoCorrespondente.ispb ||
        '',
      );

      if (
        nomeAtual !== novoNome ||
        codigoAtual !== novoCodigo ||
        ispbAtual !== novoIspb
      ) {
        setFormData(
          (estadoAnterior) => ({
            ...estadoAnterior,
            bancoNacional:
              novoNome,
            bancoCodigoNacional:
              novoCodigo,
            bancoIspbNacional:
              novoIspb,
          }),
        );
      }
    }, [
      bancos,
      formData
        ?.bancoCodigoNacional,
      formData
        ?.bancoIspbNacional,
      formData
        ?.bancoNacional,
    ]);

    /*
     * Carrega as operações bancárias da Caixa através
     * do serviço interno da aplicação.
     */
    useEffect(() => {
      let componenteMontado = true;

      const carregarOperacoes =
        async () => {
          setCarregandoOperacoes(
            true,
          );

          setErroOperacoes('');

          try {
            const resultado =
              await serviceRecuperaOperacoes();

            if (!componenteMontado) {
              return;
            }

            setOperacoesCaixa(
              Array.isArray(resultado)
                ? resultado
                : [],
            );
          } catch (error) {
            console.error(
              'Erro ao recuperar operações da Caixa:',
              error,
            );

            if (!componenteMontado) {
              return;
            }

            setOperacoesCaixa([]);

            setErroOperacoes(
              'Não foi possível carregar as operações da Caixa.',
            );
          } finally {
            if (componenteMontado) {
              setCarregandoOperacoes(
                false,
              );
            }
          }
        };

      carregarOperacoes();

      return () => {
        componenteMontado = false;
      };
    }, []);

    const validarSeAlgumPreenchido =
      () => {
        let propriedades =
          Object.keys(validacao);

        if (
          props.nacionalidade ===
          refNacional
        ) {
          propriedades =
            propriedades.filter(
              (propriedade) =>
                !propriedade.includes(
                  'Internacional',
                ),
            );
        } else {
          propriedades =
            propriedades.filter(
              (propriedade) =>
                propriedade.includes(
                  'Internacional',
                ),
            );
        }

        return propriedades.some(
          (propriedade) => {
            if (!formData) {
              return false;
            }

            const valor =
              formData[propriedade];

            return (
              valor !== '' &&
              valor !== undefined &&
              valor !== null
            );
          },
        );
      };

    const validaForm = () => {
      const propriedades =
        Object.keys(validacao);

      const novaValidacao = {
        ...validacao,
      };

      const seCabeValidacao =
        validarSeAlgumPreenchido();

      propriedades.forEach(
        (propriedade) => {
          const codigoBanco =
            String(
              formData
                ?.bancoCodigoNacional ||
              '',
            );

          const seNaoCabeValidacaoNumeroCaixa =
            propriedade ===
              'numeroOperacaoNacional' &&
            props.nacionalidade ===
              refNacional &&
            codigoBanco !== '104';

          if (
            formData &&
            !seNaoCabeValidacaoNumeroCaixa
          ) {
            const seCabeValidacaoNacional =
              props.nacionalidade ===
                refNacional &&
              !propriedade.includes(
                'Internacional',
              );

            const seCabeValidacaoInternacional =
              props.nacionalidade !==
                refNacional &&
              propriedade.includes(
                'Internacional',
              );

            if (
              (
                seCabeValidacaoNacional ||
                seCabeValidacaoInternacional
              ) &&
              seCabeValidacao
            ) {
              const valor =
                formData[propriedade];

              novaValidacao[
                propriedade
              ] =
                valor !== '' &&
                valor !== undefined &&
                valor !== null;
            } else {
              novaValidacao[
                propriedade
              ] = true;
            }
          } else {
            novaValidacao[
              propriedade
            ] = true;
          }
        },
      );

      setValidacao(
        novaValidacao,
      );
    };

    useEffect(() => {
      validaForm();

      // Mantém o comportamento legado do formulário.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      formData,
      props.nacionalidade,
    ]);

    const handleChange =
      (event) => {
        let {
          name,
          value,
        } = event.target;

        if (
          name ===
          'bancoNacional'
        ) {
          const banco =
            bancos.find(
              (item) =>
                obterIdentificadorBanco(
                  item,
                ) === value,
            );

          setBancoSelecionadoId(
            banco
              ? value
              : '',
          );

          setFormData(
            (estadoAnterior) => ({
              ...estadoAnterior,

              bancoNacional:
                banco?.nome ||
                '',

              bancoCodigoNacional:
                banco?.codigo !==
                  undefined &&
                banco?.codigo !==
                  null
                  ? String(
                    banco.codigo,
                  )
                  : '',

              bancoIspbNacional:
                banco?.ispb
                  ? String(
                    banco.ispb,
                  )
                  : '',

              numeroOperacaoNacional:
                '',
            }),
          );

          return;
        }

        value =
          limiteTamanhoCampos(
            name,
            value,
          );

        setFormData(
          (estadoAnterior) => ({
            ...estadoAnterior,
            [name]: value,
          }),
        );
      };

    useImperativeHandle(
      ref,
      () => ({
        validarFormulario() {
          return !Object
            .values(validacao)
            .some(
              (valor) => !valor,
            );
        },

        getDados() {
          return formData;
        },
      }),
      [
        formData,
        validacao,
      ],
    );

    const codigoBancoNacional =
      String(
        formData
          ?.bancoCodigoNacional ||
        '',
      );

    const bancoCaixaSelecionado =
      codigoBancoNacional ===
      '104';

    return (
      <>
        <Box
          sx={{
            flexGrow: 1,
            p: 2,
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
          >
            Dados de pagamento
          </Typography>

          {erroBancos && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
              }}
            >
              {erroBancos}
            </Alert>
          )}

          {props.nacionalidade ===
            refNacional && (
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  fullWidth
                  label="Banco"
                  name="bancoNacional"
                  value={
                    bancoSelecionadoId
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  required
                  disabled={
                    carregandoBancos
                  }
                  error={
                    !validacao
                      .bancoNacional &&
                    props
                      .nacionalidade ===
                      refNacional
                  }
                  helperText={
                    carregandoBancos
                      ? 'Carregando bancos...'
                      : ''
                  }
                >
                  {carregandoBancos && (
                    <MenuItem
                      value=""
                      disabled
                    >
                      <CircularProgress
                        size={18}
                        sx={{
                          mr: 1,
                        }}
                      />

                      Carregando bancos...
                    </MenuItem>
                  )}

                  {!carregandoBancos &&
                    bancos.length ===
                      0 && (
                      <MenuItem
                        value=""
                        disabled
                      >
                        Nenhum banco
                        disponível
                      </MenuItem>
                    )}

                  {bancos.map(
                    (banco) => {
                      const identificador =
                        obterIdentificadorBanco(
                          banco,
                        );

                      return (
                        <MenuItem
                          key={
                            identificador
                          }
                          value={
                            identificador
                          }
                        >
                          {banco.codigo}
                          {' - '}
                          {banco.nome}
                        </MenuItem>
                      );
                    },
                  )}
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Cód. do Banco"
                  name="bancoCodigoNacional"
                  value={
                    formData
                      ?.bancoCodigoNacional ||
                    ''
                  }
                  variant="outlined"
                  margin="normal"
                  required
                  disabled
                  error={
                    !validacao
                      .bancoCodigoNacional &&
                    props
                      .nacionalidade ===
                      refNacional
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Agência"
                  name="bancoAgenciaNacional"
                  value={
                    formData
                      ?.bancoAgenciaNacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  type="number"
                  required
                  error={
                    !validacao
                      .bancoAgenciaNacional &&
                    props
                      .nacionalidade ===
                      refNacional
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="DV (Agência)"
                  name="bancoAgenciaDvNacional"
                  value={
                    formData
                      ?.bancoAgenciaDvNacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  inputProps={{
                    maxLength: 1,
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Conta"
                  name="bancoContaNacional"
                  value={
                    formData
                      ?.bancoContaNacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  type="number"
                  required
                  error={
                    !validacao
                      .bancoContaNacional &&
                    props
                      .nacionalidade ===
                      refNacional
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="DV (Conta)"
                  name="bancoContaDvNacional"
                  value={
                    formData
                      ?.bancoContaDvNacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  required
                  inputProps={{
                    maxLength: 1,
                  }}
                  error={
                    !validacao
                      .bancoContaDvNacional &&
                    props
                      .nacionalidade ===
                      refNacional
                  }
                />
              </Grid>

              {bancoCaixaSelecionado && (
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <TextField
                    select
                    fullWidth
                    label="Número da Operação"
                    name="numeroOperacaoNacional"
                    value={
                      formData
                        ?.numeroOperacaoNacional ||
                      ''
                    }
                    onChange={
                      handleChange
                    }
                    variant="outlined"
                    margin="normal"
                    required
                    disabled={
                      carregandoOperacoes
                    }
                    error={
                      !validacao
                        .numeroOperacaoNacional &&
                      props
                        .nacionalidade ===
                        refNacional &&
                      bancoCaixaSelecionado
                    }
                    helperText={
                      erroOperacoes ||
                      (
                        carregandoOperacoes
                          ? 'Carregando operações...'
                          : ''
                      )
                    }
                  >
                    {carregandoOperacoes && (
                      <MenuItem
                        value=""
                        disabled
                      >
                        <CircularProgress
                          size={18}
                          sx={{
                            mr: 1,
                          }}
                        />

                        Carregando operações...
                      </MenuItem>
                    )}

                    {!carregandoOperacoes &&
                      operacoesCaixa
                        .length ===
                        0 && (
                        <MenuItem
                          value=""
                          disabled
                        >
                          Nenhuma operação
                          disponível
                        </MenuItem>
                      )}

                    {operacoesCaixa.map(
                      (
                        operacao,
                        indice,
                      ) => {
                        const valorOperacao =
                          typeof operacao ===
                          'object'
                            ? (
                              operacao.codigo ||
                              operacao.numero ||
                              operacao.valor ||
                              operacao.id ||
                              ''
                            )
                            : operacao;

                        const descricaoOperacao =
                          typeof operacao ===
                          'object'
                            ? (
                              operacao.nome ||
                              operacao.descricao ||
                              valorOperacao
                            )
                            : operacao;

                        return (
                          <MenuItem
                            key={`${valorOperacao}-${indice}`}
                            value={
                              valorOperacao
                            }
                          >
                            {
                              descricaoOperacao
                            }
                          </MenuItem>
                        );
                      },
                    )}
                  </TextField>
                </Grid>
              )}

              <Grid
                container
                margin={2}
              >
                <Grid
                  item
                  sm={4}
                  xs={12}
                >
                  <FormControl
                    component="fieldset"
                    sx={{
                      height: '100%',
                      width: '80%',
                    }}
                    required
                    error={
                      !validacao
                        .bancoTipoContaNacional &&
                      props
                        .nacionalidade ===
                        refNacional
                    }
                  >
                    <FormLabel component="legend">
                      Tipo de conta
                    </FormLabel>

                    <RadioGroup
                      row
                      name="bancoTipoContaNacional"
                      value={
                        formData
                          ?.bancoTipoContaNacional ||
                        ''
                      }
                      onChange={
                        handleChange
                      }
                      sx={{
                        display:
                          'flex',
                        justifyContent:
                          'space-between',
                      }}
                    >
                      <Grid
                        sx={{
                          display:
                            'flex',
                          flexDirection:
                            'column',
                        }}
                      >
                        <FormControlLabel
                          value="CC"
                          control={
                            <Radio />
                          }
                          label="Conta Corrente"
                        />

                        <FormControlLabel
                          value="CP"
                          control={
                            <Radio />
                          }
                          label="Conta Poupança"
                        />
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  sm={4}
                  xs={12}
                >
                  <FormControl
                    component="fieldset"
                    sx={{
                      height: '100%',
                      width: '80%',
                    }}
                    required
                    error={
                      !validacao
                        .bancoNaturezaContaNacional &&
                      props
                        .nacionalidade ===
                        refNacional
                    }
                  >
                    <FormLabel component="legend">
                      Natureza da Conta
                    </FormLabel>

                    <RadioGroup
                      row
                      name="bancoNaturezaContaNacional"
                      value={
                        formData
                          ?.bancoNaturezaContaNacional ||
                        ''
                      }
                      onChange={
                        handleChange
                      }
                      sx={{
                        display:
                          'flex',
                        justifyContent:
                          'space-between',
                      }}
                    >
                      <Grid
                        sx={{
                          display:
                            'flex',
                          flexDirection:
                            'column',
                        }}
                      >
                        <FormControlLabel
                          value="N"
                          control={
                            <Radio />
                          }
                          label="Conta Individual"
                        />

                        <FormControlLabel
                          value="S"
                          control={
                            <Radio />
                          }
                          label="Conta Conjunta"
                        />
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  sm={4}
                  xs={12}
                >
                  <FormControl
                    component="fieldset"
                    sx={{
                      height: '100%',
                      width: '80%',
                    }}
                    required
                    error={
                      !validacao
                        .bancoPosicaoContaNacional &&
                      props
                        .nacionalidade ===
                        refNacional
                    }
                  >
                    <FormLabel component="legend">
                      Posição do Titular
                    </FormLabel>

                    <RadioGroup
                      row
                      name="bancoPosicaoContaNacional"
                      value={
                        formData
                          ?.bancoPosicaoContaNacional ||
                        ''
                      }
                      onChange={
                        handleChange
                      }
                      sx={{
                        display:
                          'flex',
                        justifyContent:
                          'space-between',
                      }}
                    >
                      <Grid
                        sx={{
                          display:
                            'flex',
                          flexDirection:
                            'column',
                        }}
                      >
                        <FormControlLabel
                          value="S"
                          control={
                            <Radio />
                          }
                          label="1º Correntista"
                        />

                        <FormControlLabel
                          value="N"
                          control={
                            <Radio />
                          }
                          label="2º Correntista"
                        />
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          )}

          {props.nacionalidade !==
            refNacional && (
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Banco Internacional"
                  name="bancoInternacional"
                  value={
                    formData
                      ?.bancoInternacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  required
                  error={
                    !validacao
                      .bancoInternacional &&
                    props
                      .nacionalidade !==
                      refNacional
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Agência"
                  name="bancoAgenciaInternacional"
                  value={
                    formData
                      ?.bancoAgenciaInternacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  type="number"
                  required
                  error={
                    !validacao
                      .bancoAgenciaInternacional &&
                    props
                      .nacionalidade !==
                      refNacional
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Código IBAN"
                  name="bancoIbanInternacional"
                  value={
                    formData
                      ?.bancoIbanInternacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  required
                  error={
                    !validacao
                      .bancoIbanInternacional &&
                    props
                      .nacionalidade !==
                      refNacional
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Conta"
                  name="bancoContaInternacional"
                  value={
                    formData
                      ?.bancoContaInternacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  required
                  error={
                    !validacao
                      .bancoContaInternacional &&
                    props
                      .nacionalidade !==
                      refNacional
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Código SWIFT"
                  name="bancoSwiftInternacional"
                  value={
                    formData
                      ?.bancoSwiftInternacional ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  margin="normal"
                  required
                  error={
                    !validacao
                      .bancoSwiftInternacional &&
                    props
                      .nacionalidade !==
                      refNacional
                  }
                />
              </Grid>

              <Grid
                item
                sm={4}
                xs={12}
              >
                <FormControl
                  component="fieldset"
                  sx={{
                    height: '100%',
                    width: '80%',
                  }}
                  required
                  error={
                    !validacao
                      .bancoTipoContaInternacional &&
                    props
                      .nacionalidade !==
                      refNacional
                  }
                >
                  <FormLabel component="legend">
                    Tipo de conta
                  </FormLabel>

                  <RadioGroup
                    row
                    name="bancoTipoContaInternacional"
                    value={
                      formData
                        ?.bancoTipoContaInternacional ||
                      ''
                    }
                    onChange={
                      handleChange
                    }
                  >
                    <Grid
                      sx={{
                        display:
                          'flex',
                        flexDirection:
                          'column',
                      }}
                    >
                      <FormControlLabel
                        value="CC"
                        control={
                          <Radio />
                        }
                        label="Conta Corrente"
                      />

                      <FormControlLabel
                        value="CP"
                        control={
                          <Radio />
                        }
                        label="Conta Poupança"
                      />
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid
                item
                sm={4}
                xs={12}
              >
                <FormControl
                  component="fieldset"
                  sx={{
                    height: '100%',
                    width: '80%',
                  }}
                  required
                  error={
                    !validacao
                      .bancoNaturezaContaInternacional &&
                    props
                      .nacionalidade !==
                      refNacional
                  }
                >
                  <FormLabel component="legend">
                    Natureza da Conta
                  </FormLabel>

                  <RadioGroup
                    row
                    name="bancoNaturezaContaInternacional"
                    value={
                      formData
                        ?.bancoNaturezaContaInternacional ||
                      ''
                    }
                    onChange={
                      handleChange
                    }
                  >
                    <Grid
                      sx={{
                        display:
                          'flex',
                        flexDirection:
                          'column',
                      }}
                    >
                      <FormControlLabel
                        value="N"
                        control={
                          <Radio />
                        }
                        label="Conta Individual"
                      />

                      <FormControlLabel
                        value="S"
                        control={
                          <Radio />
                        }
                        label="Conta Conjunta"
                      />
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid
                item
                sm={4}
                xs={12}
              >
                <FormControl
                  component="fieldset"
                  sx={{
                    height: '100%',
                    width: '80%',
                  }}
                  required
                  error={
                    !validacao
                      .bancoPosicaoContaInternacional &&
                    props
                      .nacionalidade !==
                      refNacional
                  }
                >
                  <FormLabel component="legend">
                    Posição do Titular
                  </FormLabel>

                  <RadioGroup
                    row
                    name="bancoPosicaoContaInternacional"
                    value={
                      formData
                        ?.bancoPosicaoContaInternacional ||
                      ''
                    }
                    onChange={
                      handleChange
                    }
                  >
                    <Grid
                      sx={{
                        display:
                          'flex',
                        flexDirection:
                          'column',
                      }}
                    >
                      <FormControlLabel
                        value="S"
                        control={
                          <Radio />
                        }
                        label="1º Correntista"
                      />

                      <FormControlLabel
                        value="N"
                        control={
                          <Radio />
                        }
                        label="2º Correntista"
                      />
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </Box>
      </>
    );
  },
);

FormularioBancario.displayName =
  'FormularioBancario';

export default FormularioBancario;